import styled from "@emotion/styled";
import { useCallback, useState, type CSSProperties, type ComponentProps } from "react";

import {
  Form,
  FormField,
  FormikErrorMessages,
  FormikForm,
  PropertiesPanelSection,
  SkiliticsDuotoneIcon,
} from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";

import { AlignmentGrid } from "./AlignmentGrid";
import { useEditingCss } from "./EditingCssPropertiesContext";
import { FieldCssInput } from "./FieldCssInput";
import { LayoutPicker } from "./LayoutPicker";

const Column = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 5px;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 5px;
  margin-bottom: 10px;
  font-size: ${({ theme }) => theme.fontSizeSmall};
  line-height: 16px;
`;

type FormValues = Pick<
  CSSProperties,
  "paddingLeft" | "paddingRight" | "paddingTop" | "paddingBottom" | "rowGap" | "columnGap"
>;

export const FlowProperties = () => {
  const formatMessage = useFormatMessage(formatStrings);
  const { cssProperties, onCssPropertiesChange, layoutSettings, onLayoutSettingsChange } = useEditingCss();
  const [focussedInput, setFocussedInput] = useState<keyof FormValues | undefined>(undefined);

  const handleAlignmentChange: ComponentProps<typeof AlignmentGrid>["onAlignmentChange"] = useCallback(
    (value) => onLayoutSettingsChange({ alignment: value }),
    [onLayoutSettingsChange],
  );

  const handleLayoutChange: ComponentProps<typeof LayoutPicker>["onLayoutChange"] = useCallback(
    (layout) => {
      if (layout === "stacked") {
        onCssPropertiesChange({ flexDirection: undefined, display: "grid" });
      } else {
        onCssPropertiesChange({ flexDirection: layout, display: "flex" });
      }
    },
    [onCssPropertiesChange],
  );

  const handleWrapChange: ComponentProps<typeof LayoutPicker>["onWrapChange"] = useCallback(
    (wrap) => onCssPropertiesChange({ flexWrap: wrap }),
    [onCssPropertiesChange],
  );

  const handleSubmit = useCallback(
    (values: FormValues) => onCssPropertiesChange({ ...values }),
    [onCssPropertiesChange],
  );

  const handleBlur = () => setFocussedInput(undefined);

  const initialValues: FormValues = {
    paddingLeft: cssProperties.paddingLeft ?? "",
    paddingRight: cssProperties.paddingRight ?? "",
    paddingTop: cssProperties.paddingTop ?? "",
    paddingBottom: cssProperties.paddingBottom ?? "",
    rowGap: cssProperties.rowGap ?? "",
    columnGap: cssProperties.columnGap ?? "",
  };

  const layoutValue = cssProperties.display === "grid" ? "stacked" : (cssProperties.flexDirection ?? "row");

  return (
    <PropertiesPanelSection title={formatMessage("sectionHeading")}>
      <LayoutPicker
        layout={layoutValue}
        onLayoutChange={handleLayoutChange}
        onWrapChange={handleWrapChange}
        wrap={cssProperties.flexWrap ?? "nowrap"}
      />
      <FormikForm<FormValues>
        initialValues={initialValues}
        onSubmit={handleSubmit}
        submitOnBlur
        dataTest="flow-properties-form"
        validateOnChange={false}
      >
        <Form>
          <Row>
            <Column>
              <FormField<FormValues> name="paddingLeft">
                <FieldCssInput
                  placeholder="0px"
                  onFocus={() => setFocussedInput("paddingLeft")}
                  onBlur={handleBlur}
                  label={<SkiliticsDuotoneIcon icon="side-padding" />}
                  tooltip={formatMessage("paddingLeft")}
                  {...tabIndexProps(1)}
                />
              </FormField>

              <FormField<FormValues> name="paddingRight">
                <FieldCssInput
                  placeholder="0px"
                  onFocus={() => setFocussedInput("paddingRight")}
                  onBlur={handleBlur}
                  label={<SkiliticsDuotoneIcon icon="side-padding" flip="horizontal" />}
                  tooltip={formatMessage("paddingRight")}
                  {...tabIndexProps(3)}
                />
              </FormField>

              <FormField<FormValues> name="rowGap">
                <FieldCssInput
                  placeholder="0px"
                  onFocus={() => setFocussedInput("rowGap")}
                  onBlur={handleBlur}
                  label={<SkiliticsDuotoneIcon icon="gap-padding" rotation={90} />}
                  tooltip={formatMessage("rowGap")}
                  {...tabIndexProps(5)}
                />
              </FormField>
            </Column>

            <Column>
              <AlignmentGrid
                activeAlignment={layoutSettings.alignment}
                onAlignmentChange={handleAlignmentChange}
                focussedInput={focussedInput}
                flexDirection={cssProperties.flexDirection}
              />
            </Column>

            <Column>
              <FormField<FormValues> name="paddingTop">
                <FieldCssInput
                  placeholder="0px"
                  onFocus={() => setFocussedInput("paddingTop")}
                  onBlur={handleBlur}
                  label={<SkiliticsDuotoneIcon icon="side-padding" rotation={90} />}
                  tooltip={formatMessage("paddingTop")}
                  {...tabIndexProps(2)}
                />
              </FormField>

              <FormField<FormValues> name="paddingBottom">
                <FieldCssInput
                  placeholder="0px"
                  onFocus={() => setFocussedInput("paddingBottom")}
                  onBlur={handleBlur}
                  label={<SkiliticsDuotoneIcon icon="side-padding" rotation={270} />}
                  tooltip={formatMessage("paddingBottom")}
                  {...tabIndexProps(4)}
                />
              </FormField>

              <FormField<FormValues> name="columnGap">
                <FieldCssInput
                  placeholder="0px"
                  onFocus={() => setFocussedInput("columnGap")}
                  onBlur={handleBlur}
                  label={<SkiliticsDuotoneIcon icon="gap-padding" />}
                  tooltip={formatMessage("columnGap")}
                  {...tabIndexProps(6)}
                />
              </FormField>
            </Column>
          </Row>
          <FormikErrorMessages />
        </Form>
      </FormikForm>
    </PropertiesPanelSection>
  );
};

const tabIndexProps = (index: number) => ({
  slotProps: {
    htmlInput: {
      tabIndex: index,
    },
  },
});

const formatStrings = {
  sectionHeading: {
    en: "Flow, Alignment & Padding",
  },
  paddingLeft: {
    en: "Left Padding",
  },
  paddingRight: {
    en: "Right Padding",
  },
  paddingTop: {
    en: "Top Padding",
  },
  paddingBottom: {
    en: "Bottom Padding",
  },
  rowGap: {
    en: "Row Gap",
  },
  columnGap: {
    en: "Column Gap",
  },
} satisfies FormatMessages;
